import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, AlertTriangle, ShieldCheck, ShieldAlert,
  FileWarning, Wand2, Download, Calculator, FileSearch, X, CheckCircle2,
  ListChecks, ChevronDown, ChevronUp, History, CalendarClock, Filter, CheckSquare, Square, RotateCcw,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { RequirePermission } from '../../components/permissions/RequirePermission';
import { PERMS } from '../../utils/permissions';
import { usePermissions } from '../../contexts/PermissionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_LEVEL_COLOR = {
  excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', icon: CheckCircle2 },
  good: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', icon: ShieldCheck },
  warning: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', icon: AlertTriangle },
  critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', icon: ShieldAlert },
};

const fmtXOF = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';

/**
 * PM Spec 09/05/2026 — Cockpit qualité comptable permanent.
 * Détection en temps réel des doublons et incohérences pouvant fausser les KPIs.
 */
const FinanceAuditPage = () => {
  const [data, setData] = useState(null);
  const [legacyData, setLegacyData] = useState(null);
  const [legacySelected, setLegacySelected] = useState(new Set());
  const [legacyFilters, setLegacyFilters] = useState({ category: 'all', period: 'all', status: 'all', search: '' });
  const [legacyConfirmOpen, setLegacyConfirmOpen] = useState(false);
  const [legacyDryRunResult, setLegacyDryRunResult] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({ spot: true, contract: true, missing: false, orphan: false, inconsistent: false, noClient: true, legacy: true });
  const [cancelModal, setCancelModal] = useState({ open: false, invoiceId: null, invoiceNumber: null });
  const [cancelReason, setCancelReason] = useState('');
  const [reversals, setReversals] = useState(null);

  const authHeaders = useMemo(() => {
    // SEC-003 — auth via cookie HttpOnly + withCredentials (le header Bearer est sentinelle)
    return { Authorization: 'Bearer cookie-auth' };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, alertsR, legacyR, reversalsR] = await Promise.all([
        axios.get(`${API}/admin/finance-audit/duplicates`, { headers: authHeaders }),
        axios.get(`${API}/admin/finance-audit/alerts?only_open=true`, { headers: authHeaders }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/admin/finance-audit/legacy-and-misaligned`, { headers: authHeaders }).catch(() => ({ data: { count: 0, items: [], summary: {} } })),
        axios.get(`${API}/admin/finance-audit/payment-reversals?days=30`, { headers: authHeaders }).catch(() => ({ data: { count: 0, totalReversedAmount: 0, reversals: [] } })),
      ]);
      setData(r.data);
      setAlerts(alertsR.data?.alerts || []);
      setLegacyData(legacyR.data);
      setReversals(reversalsR.data);
    } catch (err) {
      toast({
        title: 'Erreur de chargement',
        description: err?.response?.data?.detail || 'Impossible de charger l\'audit.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/admin/finance-audit/alerts/${alertId}/acknowledge`, {}, { headers: authHeaders });
      toast({ title: 'Alerte acquittée' });
      await fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  const handleRunMonitor = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/finance-audit/run-monitor`, {}, { headers: authHeaders });
      toast({ title: 'Monitoring exécuté', description: `Action : ${r.data.action} (score ${r.data.score})` });
      await fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRenumberAllSpot = async () => {
    if (!data?.spotInvoiceDuplicates?.items?.length) return;
    if (!window.confirm(`Renuméroter toutes les ops dupliquées (sauf le 1er duplicata) ? Cette action est tracée en audit.`)) return;
    // Collecter tous les opIds à renuméroter (skip the first per group)
    const opIds = [];
    data.spotInvoiceDuplicates.items.forEach(group => {
      const sorted = [...group.ops].sort((a, b) =>
        String(a.invoiceGeneratedAt || '').localeCompare(String(b.invoiceGeneratedAt || ''))
      );
      sorted.slice(1).forEach(op => opIds.push(op.id));
    });
    if (!opIds.length) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/finance-audit/spot/renumber`,
        { operationIds: opIds, dryRun: false },
        { headers: authHeaders }
      );
      toast({
        title: 'Renumérotation effectuée',
        description: `${r.data.count} opération(s) ont reçu un nouveau numéro unique.`,
      });
      await fetchData();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Échec.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  // Filter + selection helpers for legacy list
  const legacyFilteredItems = useMemo(() => {
    const items = legacyData?.items || [];
    return items.filter(it => {
      if (legacyFilters.category !== 'all' && it.category !== legacyFilters.category) return false;
      if (legacyFilters.status !== 'all' && it.financialStatus !== legacyFilters.status) return false;
      if (legacyFilters.period !== 'all') {
        const ym = (it.operationDate || '').slice(0, 7);
        if (ym !== legacyFilters.period) return false;
      }
      if (legacyFilters.search) {
        const s = legacyFilters.search.toLowerCase();
        const hit = (it.operationNumber || '').toLowerCase().includes(s)
          || (it.currentInvoiceNumber || '').toLowerCase().includes(s)
          || (it.client || '').toLowerCase().includes(s);
        if (!hit) return false;
      }
      return true;
    });
  }, [legacyData, legacyFilters]);

  const legacyAvailablePeriods = useMemo(() => {
    const set = new Set();
    (legacyData?.items || []).forEach(it => {
      const ym = (it.operationDate || '').slice(0, 7);
      if (ym) set.add(ym);
    });
    return Array.from(set).sort();
  }, [legacyData]);

  const toggleLegacySelected = (id) => {
    setLegacySelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setLegacySelected(new Set(legacyFilteredItems.map(it => it.opId)));
  };

  const clearSelection = () => setLegacySelected(new Set());

  const handleLegacyOpenConfirm = async () => {
    if (legacySelected.size === 0) {
      toast({ title: 'Aucune sélection', description: 'Sélectionnez au moins une facture à régénérer.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    setLegacyDryRunResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/finance-audit/regenerate-legacy`,
        { dryRun: true, includeSentPaid: true, operationIds: Array.from(legacySelected) },
        { headers: authHeaders }
      );
      setLegacyDryRunResult(r.data);
      setLegacyConfirmOpen(true);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec du dry-run', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleLegacyApply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/finance-audit/regenerate-legacy`,
        { dryRun: false, includeSentPaid: true, operationIds: Array.from(legacySelected) },
        { headers: authHeaders }
      );
      toast({
        title: 'Régénération terminée',
        description: `${r.data.fixed} facture(s) renumérotée(s) et réalignée(s) — ${r.data.skipped} ignorée(s)`,
      });
      setLegacyConfirmOpen(false);
      setLegacyDryRunResult(null);
      setLegacySelected(new Set());
      await fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleBackfillCompanyName = async () => {
    const items = data?.missingCompanyNameOps?.items || [];
    const resolvable = items.filter(o => o.resolvable);
    if (!resolvable.length) {
      toast({ title: 'Aucune ligne résoluble', description: 'Tous les userId manquants ou comptes sans nom.', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Compléter ${resolvable.length} nom(s) client à partir des comptes liés ? Action tracée en audit.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/finance-audit/missing-company-name/backfill`,
        { operationIds: resolvable.map(o => o.id), dryRun: false },
        { headers: authHeaders }
      );
      toast({
        title: 'Backfill terminé',
        description: `${r.data.fixed} op(s) corrigée(s) — ${r.data.skipped} ignorée(s)`,
      });
      await fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelModal.invoiceId || !cancelReason.trim()) {
      toast({ title: 'Motif requis', description: 'Indiquez la raison de l\'annulation.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/finance-audit/contract/cancel/${cancelModal.invoiceId}`,
        { reason: cancelReason.trim() },
        { headers: authHeaders }
      );
      toast({ title: 'Facture annulée', description: cancelModal.invoiceNumber });
      setCancelModal({ open: false, invoiceId: null, invoiceNumber: null });
      setCancelReason('');
      await fetchData();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Échec.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-slate-500" data-testid="audit-page-loading">
        <Loader2 className="w-8 h-8 animate-spin mr-3" /> Analyse comptable en cours…
      </div>
    );
  }

  if (!data) return null;

  const lvl = data.healthScore?.level || 'unknown';
  const levelStyle = HEALTH_LEVEL_COLOR[lvl] || HEALTH_LEVEL_COLOR.warning;
  const HealthIcon = levelStyle.icon;

  const Section = ({ id, title, icon: Icon, count, severity, kpiImpact, children }) => {
    const isOpen = expanded[id];
    return (
      <Card className={`border ${severity === 'critical' ? 'border-red-300' : severity === 'warning' ? 'border-amber-300' : 'border-slate-200'}`}>
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => setExpanded(s => ({ ...s, [id]: !s[id] }))}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
            data-testid={`audit-section-${id}`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`w-5 h-5 ${count > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
              <div className="text-left">
                <div className="text-sm font-bold text-slate-900">{title}</div>
                {kpiImpact !== undefined && kpiImpact > 0 && (
                  <div className="text-xs text-red-700">Impact KPI : {fmtXOF(kpiImpact)}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={count > 0 ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'}>
                {count}
              </Badge>
              {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </button>
          {isOpen && <div className="border-t border-slate-200 p-4">{children}</div>}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="finance-audit-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileSearch className="w-8 h-8 text-emerald-600" /> Audit Factures
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cockpit qualité comptable — détection en temps réel des doublons & incohérences.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRunMonitor} disabled={busy} className="gap-2" data-testid="audit-run-monitor-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            Vérifier maintenant
          </Button>
          <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2" data-testid="audit-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Actualiser
          </Button>
        </div>
      </div>

      {/* Bandeau alertes ouvertes (PM Spec 09/05/2026) */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="audit-alerts-banner">
          {alerts.slice(0, 3).map(alert => (
            <Card key={alert.id} className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-red-900">
                      Alerte santé comptable — Score {alert.score}/100 ({alert.level})
                    </div>
                    <div className="text-xs text-red-700 mt-1">
                      Détectée le {new Date(alert.createdAt).toLocaleString('fr-FR')}
                      {alert.lastObservedAt && alert.lastObservedAt !== alert.createdAt && (
                        <> · Dernière observation {new Date(alert.lastObservedAt).toLocaleString('fr-FR')}</>
                      )}
                    </div>
                    <div className="text-xs text-red-700 mt-1">
                      {alert.snapshot?.spotDuplicates || 0} doublons spot · {alert.snapshot?.contractDuplicates || 0} doublons contrat · {alert.snapshot?.orphanRuns || 0} runs orphelins
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAcknowledgeAlert(alert.id)}
                  className="border-red-400 text-red-700 hover:bg-red-100 shrink-0"
                  data-testid={`audit-ack-alert-${alert.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Acquitter
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Score de santé */}
      <Card className={`border-2 ${levelStyle.border} ${levelStyle.bg}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-full ${levelStyle.bg} border-2 ${levelStyle.border} flex items-center justify-center`}>
                <span className={`text-3xl font-bold ${levelStyle.text}`}>{data.healthScore.score}</span>
              </div>
              <div>
                <div className={`text-2xl font-bold ${levelStyle.text} flex items-center gap-2`}>
                  <HealthIcon className="w-6 h-6" />
                  Score : {lvl.toUpperCase()}
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Calculé à {data.computedAt && new Date(data.computedAt).toLocaleString('fr-FR')}
                </div>
              </div>
            </div>
            {data.totalKpiImpact > 0 && (
              <div className="text-right">
                <div className="text-xs text-slate-500 uppercase font-bold">Impact KPI total potentiel</div>
                <div className="text-2xl font-bold text-red-700" data-testid="audit-total-kpi-impact">
                  {fmtXOF(data.totalKpiImpact)}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section Spot duplicates */}
      <Section
        id="spot"
        title="Doublons numéros de facture (Opérations spot)"
        icon={FileWarning}
        count={data.spotInvoiceDuplicates.count}
        severity={data.spotInvoiceDuplicates.count > 0 ? 'critical' : 'ok'}
        kpiImpact={data.spotInvoiceDuplicates.kpiImpact}
      >
        {data.spotInvoiceDuplicates.count === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Aucun doublon détecté.
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Plusieurs opérations partagent le même numéro de facture. Le 1er duplicata par date est conservé,
                les autres seront renumérotés avec un nouveau numéro unique.
              </p>
              <Button
                size="sm"
                onClick={handleRenumberAllSpot}
                disabled={busy}
                className="bg-red-600 hover:bg-red-700 gap-2 shrink-0"
                data-testid="audit-renumber-all-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Renuméroter tout
              </Button>
            </div>
            <div className="space-y-3">
              {data.spotInvoiceDuplicates.items.map((g, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <span className="font-mono font-bold text-slate-900">{g.invoiceNumber}</span>
                      <Badge variant="outline" className="ml-2">{g.count}x</Badge>
                      {g.uniqueClients > 1 && (
                        <Badge className="ml-2 bg-red-600 text-white">⚠ {g.uniqueClients} clients différents</Badge>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">Total montant: {fmtXOF(g.totalAmount)}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    {g.ops.map((op, i) => (
                      <div key={op.id} className={`p-2 rounded border ${i === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex items-center gap-2">
                          {i === 0 && <Badge className="bg-emerald-600 text-white text-[10px]">Conservé</Badge>}
                          <span className="font-mono">{op.operationNumber}</span>
                        </div>
                        <div className="text-slate-600">{op.companyName || '—'} · {fmtXOF(op.invoiceAmount)} · {op.financialStatus}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* Section Contract duplicates */}
      <Section
        id="contract"
        title="Doublons factures contrat (même contrat + même période)"
        icon={Calculator}
        count={data.contractInvoiceDuplicates.count}
        severity={data.contractInvoiceDuplicates.count > 0 ? 'critical' : 'ok'}
        kpiImpact={data.contractInvoiceDuplicates.kpiImpact}
      >
        {data.contractInvoiceDuplicates.count === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Aucune facture contrat doublonnée.
          </div>
        ) : (
          <div className="space-y-3">
            {data.contractInvoiceDuplicates.items.map((g, idx) => (
              <div key={idx} className="border border-red-200 rounded-lg p-3 bg-red-50/30">
                <div className="text-sm font-bold text-slate-900 mb-2">
                  {g.contractNumber} · {g.clientName} · {g.periodStart} → {g.periodEnd}
                </div>
                <div className="space-y-2">
                  {g.invoices.map((inv, i) => (
                    <div key={inv.id} className="flex items-center justify-between p-2 bg-white border rounded text-xs">
                      <div className="flex items-center gap-2">
                        {i === 0 ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]">Conservée</Badge>
                        ) : (
                          <Badge className="bg-red-600 text-white text-[10px]">Doublon</Badge>
                        )}
                        <span className="font-mono">{inv.invoiceNumber}</span>
                        <span className="text-slate-500">status={inv.status} · {fmtXOF(inv.montantTotalTTC)}</span>
                      </div>
                      {i > 0 && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setCancelModal({ open: true, invoiceId: inv.id, invoiceNumber: inv.invoiceNumber })}
                          className="border-red-400 text-red-700 hover:bg-red-50 h-7 px-2 text-[11px]"
                          data-testid={`audit-cancel-contract-${inv.id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Annuler
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Section Missing periods */}
      <Section
        id="missing"
        title="Factures contrat sans période"
        icon={ListChecks}
        count={data.missingPeriodContractInvoices.count}
        severity={data.missingPeriodContractInvoices.count > 0 ? 'warning' : 'ok'}
      >
        {data.missingPeriodContractInvoices.count === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Toutes les factures contrat ont une période.
          </div>
        ) : (
          <div className="space-y-2">
            {data.missingPeriodContractInvoices.items.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                <span><span className="font-mono">{inv.invoiceNumber}</span> · {inv.clientName} · status={inv.status} · {fmtXOF(inv.montantTotalTTC)}</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setCancelModal({ open: true, invoiceId: inv.id, invoiceNumber: inv.invoiceNumber })}
                  className="h-7 px-2 text-[11px]"
                  data-testid={`audit-cancel-missing-${inv.id}`}
                >
                  Annuler
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Section Orphaned runs */}
      <Section
        id="orphan"
        title="Runs orphelins (marqués facturés mais facture absente/annulée)"
        icon={AlertTriangle}
        count={data.orphanedRuns.count}
        severity={data.orphanedRuns.count > 0 ? 'warning' : 'ok'}
      >
        {data.orphanedRuns.count === 0 ? (
          <div className="text-sm text-emerald-700">Aucun run orphelin.</div>
        ) : (
          <div className="text-xs space-y-1 max-h-60 overflow-y-auto">
            {data.orphanedRuns.items.map((r, i) => (
              <div key={i} className="p-2 bg-amber-50 border border-amber-200 rounded">
                Run <span className="font-mono">{r.id?.slice(0,8)}</span> · {r.date} · contrat {r.contractId?.slice(0,8)}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Section Missing companyName (factures sans nom client) */}
      <Section
        id="noClient"
        title="Factures sans nom client (affichage 'Inconnu')"
        icon={FileWarning}
        count={data.missingCompanyNameOps?.count || 0}
        severity={(data.missingCompanyNameOps?.count || 0) > 0 ? 'warning' : 'ok'}
      >
        {(data.missingCompanyNameOps?.count || 0) === 0 ? (
          <div className="text-sm text-emerald-700">Toutes les factures ont un client identifié.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded">
              <div className="text-xs text-amber-900">
                <strong>{data.missingCompanyNameOps.resolvableCount}</strong> sur {data.missingCompanyNameOps.count}{' '}
                ligne(s) peuvent être corrigées automatiquement depuis le compte utilisateur lié.
              </div>
              {data.missingCompanyNameOps.resolvableCount > 0 && (
                <Button
                  onClick={handleBackfillCompanyName}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="audit-backfill-company-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                  Compléter en 1 clic
                </Button>
              )}
            </div>
            <div className="space-y-1 text-xs max-h-72 overflow-y-auto">
              {data.missingCompanyNameOps.items.map(op => (
                <div
                  key={op.id}
                  className={`p-2 rounded border flex items-center justify-between gap-3 ${
                    op.resolvable ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'
                  }`}
                  data-testid={`audit-missing-name-row-${op.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-slate-700">{op.operationNumber}</span>
                      <Badge variant="outline" className="text-[10px]">{op.invoiceNumber}</Badge>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-600">{fmtXOF(op.invoiceAmount)}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-600">{op.financialStatus}</span>
                    </div>
                    <div className="mt-1 text-slate-600">
                      {op.resolvable ? (
                        <>Suggestion : <strong className="text-emerald-700">{op.suggestedCompanyName}</strong></>
                      ) : (
                        <span className="text-red-700">Non résoluble (userId manquant ou compte sans nom).</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Section Factures legacy + dates désalignées */}
      <Section
        id="legacy"
        title="Factures legacy & dates désalignées"
        icon={CalendarClock}
        count={legacyData?.count || 0}
        severity={(legacyData?.count || 0) > 0 ? 'critical' : 'ok'}
      >
        {(legacyData?.count || 0) === 0 ? (
          <div className="text-sm text-emerald-700">Toutes les factures ont un format standard et une date alignée sur leur opération.</div>
        ) : (
          <div className="space-y-3">
            {/* Header impact + action sélection */}
            <div className="flex items-start justify-between p-3 bg-red-50 border border-red-200 rounded gap-3">
              <div className="text-xs text-red-900 flex-1">
                <div className="font-bold mb-1">⚠️ Impact KPIs majeur</div>
                <div>
                  <strong>{legacyData.count}</strong> facture(s) détectée(s) :
                  {' '}<Badge variant="outline" className="text-[10px] bg-white">{legacyData.summary?.legacy_n_format || 0} legacy N°XXX</Badge>
                  {' '}<Badge variant="outline" className="text-[10px] bg-white">{legacyData.summary?.migr_format || 0} MIGR</Badge>
                  {' '}<Badge variant="outline" className="text-[10px] bg-white">{legacyData.summary?.other || 0} autres</Badge>
                </div>
                <div className="mt-1">
                  Désalignements : <strong className="text-red-700">{legacyData.summary?.year_misaligned || 0} année</strong>{' '}+{' '}<strong className="text-amber-700">{legacyData.summary?.month_misaligned || 0} mois</strong>
                </div>
              </div>
              <Button
                onClick={handleLegacyOpenConfirm}
                disabled={busy || legacySelected.size === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap disabled:opacity-50"
                data-testid="audit-regenerate-legacy-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                Régénérer la sélection ({legacySelected.size})
              </Button>
            </div>

            {/* Barre de filtres + sélection bulk */}
            <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                value={legacyFilters.search}
                onChange={(e) => setLegacyFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Rechercher op / facture / client…"
                className="px-2 py-1 border border-slate-300 rounded text-xs flex-1 min-w-[160px] focus:outline-none focus:ring-1 focus:ring-emerald-400"
                data-testid="legacy-search"
              />
              <select
                value={legacyFilters.category}
                onChange={(e) => setLegacyFilters(f => ({ ...f, category: e.target.value }))}
                className="px-2 py-1 border border-slate-300 rounded text-xs bg-white"
                data-testid="legacy-filter-category"
              >
                <option value="all">Toutes catégories</option>
                <option value="legacy_n_format">Legacy N°XXX</option>
                <option value="migr_format">MIGR</option>
                <option value="fact_standard">FACT standard désaligné</option>
                <option value="other">Autres</option>
              </select>
              <select
                value={legacyFilters.period}
                onChange={(e) => setLegacyFilters(f => ({ ...f, period: e.target.value }))}
                className="px-2 py-1 border border-slate-300 rounded text-xs bg-white"
                data-testid="legacy-filter-period"
              >
                <option value="all">Toutes périodes</option>
                {legacyAvailablePeriods.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                value={legacyFilters.status}
                onChange={(e) => setLegacyFilters(f => ({ ...f, status: e.target.value }))}
                className="px-2 py-1 border border-slate-300 rounded text-xs bg-white"
                data-testid="legacy-filter-status"
              >
                <option value="all">Tous statuts</option>
                <option value="invoiced">invoiced</option>
                <option value="paid">paid</option>
                <option value="partial">partial</option>
                <option value="unpaid">unpaid</option>
              </select>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={selectAllFiltered}
                  className="px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1"
                  data-testid="legacy-select-all"
                >
                  <CheckSquare className="w-3.5 h-3.5" /> Tout sélectionner ({legacyFilteredItems.length})
                </button>
                <button
                  onClick={clearSelection}
                  disabled={legacySelected.size === 0}
                  className="px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1"
                  data-testid="legacy-clear-selection"
                >
                  <Square className="w-3.5 h-3.5" /> Effacer
                </button>
              </div>
            </div>

            {/* Compteur sélection */}
            <div className="flex items-center justify-between text-xs text-slate-600">
              <div>
                <strong className="text-slate-900">{legacyFilteredItems.length}</strong> ligne(s) affichée(s) ·{' '}
                <strong className={legacySelected.size > 0 ? 'text-emerald-700' : 'text-slate-500'}>{legacySelected.size}</strong> sélectionnée(s)
              </div>
              <div className="text-slate-500 italic">
                Format cible : <code className="px-1 py-0.5 bg-slate-100 rounded">FACT-{'{YYYY}-{MM}-{NNNN}'}</code> · Date facture = date opération
              </div>
            </div>

            {/* Tableau dense avec checkboxes */}
            <div className="border border-slate-200 rounded max-h-[28rem] overflow-y-auto">
              {legacyFilteredItems.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-xs italic">Aucun résultat — ajustez les filtres.</div>
              ) : legacyFilteredItems.map(it => {
                const isSelected = legacySelected.has(it.opId);
                return (
                  <div
                    key={it.opId}
                    className={`p-2 border-b border-slate-100 grid grid-cols-12 gap-2 items-center text-xs cursor-pointer transition-colors ${
                      isSelected ? 'bg-emerald-50' : 'bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => toggleLegacySelected(it.opId)}
                    data-testid={`audit-legacy-row-${it.opId}`}
                  >
                    <div className="col-span-1 flex justify-center">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleLegacySelected(it.opId)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`legacy-checkbox-${it.opId}`}
                      />
                    </div>
                    <div className="col-span-3 font-mono text-slate-700 truncate" title={it.operationNumber}>{it.operationNumber}</div>
                    <div className="col-span-4 flex items-center gap-1 min-w-0">
                      <span className="line-through text-red-600 truncate" title={it.currentInvoiceNumber}>{it.currentInvoiceNumber}</span>
                      <span className="text-slate-400 shrink-0">→</span>
                      <span className="font-bold text-emerald-700 truncate" title={it.suggestedNewNumber}>{it.suggestedNewNumber || '—'}</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-1 text-[11px]">
                      <span className="line-through text-red-500">{(it.currentInvoiceDate || '').slice(0, 10)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-bold text-emerald-700">{(it.operationDate || '').slice(0, 10)}</span>
                    </div>
                    <div className="col-span-2 text-right text-slate-500 text-[11px] truncate" title={it.client}>
                      {it.client} · <span className="font-medium">{it.financialStatus}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      {/* Section Inconsistent spot */}
      <Section
        id="inconsistent"
        title="Ops marquées invoiced/paid sans n° de facture"
        icon={AlertTriangle}
        count={data.inconsistentSpotInvoiced.count}
        severity={data.inconsistentSpotInvoiced.count > 0 ? 'warning' : 'ok'}
      >
        {data.inconsistentSpotInvoiced.count === 0 ? (
          <div className="text-sm text-emerald-700">Aucune incohérence.</div>
        ) : (
          <div className="space-y-1 text-xs max-h-60 overflow-y-auto">
            {data.inconsistentSpotInvoiced.items.map(op => (
              <div key={op.id} className="p-2 bg-amber-50 border border-amber-200 rounded">
                <span className="font-mono">{op.operationNumber}</span> · {op.companyName || '—'} · status={op.financialStatus} · {fmtXOF(op.estimatedPrice)}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Legacy regen confirm modal */}
      <Dialog open={legacyConfirmOpen} onOpenChange={(o) => !o && setLegacyConfirmOpen(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-emerald-600" />
              Confirmer la régénération bulk
            </DialogTitle>
          </DialogHeader>
          {legacyDryRunResult ? (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                <div className="font-bold text-emerald-900">
                  {legacyDryRunResult.fixed} facture(s) seront renumérotées et réalignées.
                </div>
                <div className="text-xs text-emerald-800 mt-1">
                  Les KPIs annuels/mensuels seront restaurés à leur valeur réelle. Toute opération est tracée en audit log et peut être réinspectée via <code>invoiceRenumberedFrom</code> et <code>invoiceDateRealignedFrom</code>.
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto text-xs space-y-1 border border-slate-200 rounded p-2">
                <div className="grid grid-cols-12 gap-2 font-bold text-slate-600 pb-1 border-b">
                  <div className="col-span-3">Opération</div>
                  <div className="col-span-4">Numéro</div>
                  <div className="col-span-3">Date facture</div>
                  <div className="col-span-2 text-right">Catégorie</div>
                </div>
                {(legacyDryRunResult.actions || []).slice(0, 80).map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 py-1 border-b border-slate-100">
                    <div className="col-span-3 font-mono text-slate-700 truncate">{a.operationNumber}</div>
                    <div className="col-span-4 flex items-center gap-1">
                      <span className="line-through text-red-600 truncate">{a.oldInvoiceNumber}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-bold text-emerald-700 truncate">{a.newInvoiceNumber}</span>
                    </div>
                    <div className="col-span-3 flex items-center gap-1 text-[11px]">
                      <span className="line-through text-red-500">{(a.oldInvoiceDate || '').slice(0, 10)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-emerald-700">{(a.newInvoiceDate || '').slice(0, 10)}</span>
                    </div>
                    <div className="col-span-2 text-right text-[10px] text-slate-500">{a.category}</div>
                  </div>
                ))}
                {(legacyDryRunResult.actions?.length || 0) > 80 && (
                  <div className="text-center text-slate-400 italic pt-1">+ {legacyDryRunResult.actions.length - 80} autres…</div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Chargement du dry-run…</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLegacyConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleLegacyApply}
              disabled={busy || !legacyDryRunResult || legacyDryRunResult.fixed === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="audit-legacy-apply-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirmer la régénération
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel modal */}
      <Dialog open={cancelModal.open} onOpenChange={(o) => !o && setCancelModal({ open: false, invoiceId: null, invoiceNumber: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler la facture {cancelModal.invoiceNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Cette facture sera marquée <strong>cancelled</strong>. Les runs liés seront libérés.
              Action tracée en audit log.
            </p>
            <Textarea
              placeholder="Motif d'annulation (obligatoire)…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              data-testid="audit-cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModal({ open: false, invoiceId: null, invoiceNumber: null })}>
              Retour
            </Button>
            <Button
              onClick={handleConfirmCancel}
              disabled={busy || !cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="audit-confirm-cancel-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SECTION : Annulations de paiement (30 derniers jours) — PM Spec 03/06/2026 */}
      {reversals && reversals.count > 0 && (
        <div className="bg-white rounded-lg shadow border border-orange-200 p-6 mt-6" data-testid="payment-reversals-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-orange-600" />
                Annulations de paiement — 30 derniers jours
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {reversals.count} annulation(s) • Montant total annulé : <strong>{reversals.totalReversedAmount.toLocaleString('fr-FR')} XOF</strong>
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Référence</th>
                  <th className="text-left p-2">Client</th>
                  <th className="text-right p-2">Montant annulé</th>
                  <th className="text-left p-2">Par</th>
                  <th className="text-left p-2">Délai</th>
                  <th className="text-left p-2">Motif</th>
                </tr>
              </thead>
              <tbody>
                {reversals.reversals.map((r, idx) => (
                  <tr key={r.id || idx} className="border-t hover:bg-gray-50" data-testid={`reversal-row-${idx}`}>
                    <td className="p-2 whitespace-nowrap text-xs">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="p-2 font-mono text-xs">
                      {r.invoiceNumber || r.operationNumber || r.resourceId?.slice(0, 8) || '—'}
                      {r.context && <span className="ml-1 text-[10px] text-gray-400">({r.context})</span>}
                    </td>
                    <td className="p-2 text-xs">{r.clientName || '—'}</td>
                    <td className="p-2 text-right font-semibold text-red-600">
                      {(r.previousAmountPaid || 0).toLocaleString('fr-FR')} XOF
                    </td>
                    <td className="p-2 text-xs">{r.userName || '—'} <span className="text-gray-400">({r.userRole})</span></td>
                    <td className="p-2 text-xs">
                      <span className={r.daysSincePayment > 60 ? 'text-amber-600 font-semibold' : 'text-gray-600'}>
                        {r.daysSincePayment != null ? `${r.daysSincePayment}j` : '—'}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-gray-700 max-w-xs truncate" title={r.reason}>{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function FinanceAuditPageGuarded(props) {
  return (
    <RequirePermission
      anyPermission={[PERMS.FINANCE_AUDIT_VIEW, PERMS.FINANCE_AUDIT_REGENERATE, PERMS.FINANCE_COCKPIT_VIEW]}
    >
      <FinanceAuditPage {...props} />
    </RequirePermission>
  );
}
